import React, { FC, useEffect, useState, ReactNode } from 'react';
import TableSimple from 'src/components/TableSimple';
import { ConstantsCodeType } from 'src/types/constantsCodes';
import { FileImportType, OrderImportErrorType } from 'src/types/ordersImport';
import { CustomRenderType, TableColumnType, TableRowType } from 'src/types/table';
import IconButton from '@mui/material/IconButton';
import WarningIcon from '@mui/icons-material/Warning';
import CheckIcon from '@mui/icons-material/Check';
import Menu from 'src/components/Menu';
import translate from '@translate';
import Typography from 'src/components/Typography';
import { OrderType } from 'src/types/order';
import { OrderIcon } from 'src/components/SkladonIcon/SmallIcons';
import { Button } from '@mui/material';
import OrderDetail from './OrderDetail';
import Tooltip from 'src/components/Tooltip';
import { styled } from '@mui/system';
import { colors } from 'src/themes/default';

const WarningButton = styled('div')({
    '& svg path': {
        fill: colors.primary_O,
    },
});

const allColumns: TableColumnType[] = [
    {
        label: 'id',
        key: 'ID',
    },
    {
        label: 'validity',
        key: 'errors',
        customRender: true,
    },
    {
        label: 'result',
        key: 'result',
        customRender: true,
    },
    {
        label: 'detail',
        key: 'detail',
        customRender: true,
    },
    {
        label: 'actions',
        key: 'actions',
        customRender: true,
    },
    {
        label: 'eshopOrderId',
        key: 'ESHOP_ORDER_ID',
    },
    {
        label: 'recipientName',
        key: 'RECIPIENT_NAME',
    },
    {
        label: 'recipientEmail',
        key: 'RECIPIENT_EMAIL',
    },
];

export interface ValidationTablePropsType {
    orderTypeCodes: ConstantsCodeType[];
    expeditionMethodCodes: ConstantsCodeType[];
    files: FileImportType[];
}

const ValidationTable: FC<ValidationTablePropsType> = (props) => {
    const [rows, setRows] = useState<OrderType[]>([]);
    const [showDetailData, setShowDetailData] = useState<TableRowType | undefined>();

    useEffect(() => {
        const newRows: OrderType[] = [];
        props.files.map((file) => {
            file.validation?.orders.map((order) => {
                newRows.push(order);
            });
        });
        setRows(newRows);
    }, [props.files]);

    const customRender: CustomRenderType = (column, row) => {
        switch (column.key) {
            case 'errors':
                if (row.validationErrors) {
                    return (
                        <WarningButton>
                            <Menu
                                variant="menu"
                                itemStyle={{ borderBottom: '1px solid silver' }}
                                icon={<WarningIcon color="error" />}
                                items={row.validationErrors.map((error: OrderImportErrorType) => (
                                    <div>
                                        <span>{`${error.dataKey}: `}</span>
                                        <span>{translate(`error.${error.label}`, error)}</span>
                                    </div>
                                ))}
                            />
                        </WarningButton>
                    );
                }
                return (
                    <IconButton disabled>
                        <CheckIcon style={{ color: 'green' }} />
                    </IconButton>
                );
            case 'detail':
                return (
                    <Button
                        onClick={() => setShowDetailData(row)}
                        title={translate('ordersImport.action.showOrderDetail')}
                    >
                        <OrderIcon />
                    </Button>
                );
            case 'result':
                if (!row.result) {
                    return ' ';
                }
                return row.result.success ? (
                    <CheckIcon style={{ color: 'green' }} />
                ) : (
                    <Tooltip title={row.result.error}>
                        <WarningIcon color="error" />
                    </Tooltip>
                );
            case 'ID':
            default:
                return translate(row.ID ? 'ordersImport.action.update' : 'ordersImport.action.create');
        }
    };

    const totalCount = { create: 0, update: 0 };
    props.files
        .filter((file) => !file.imported)
        .forEach((file) => {
            totalCount.create += file.validation?.createCount || 0;
            totalCount.update += file.validation?.updateCount || 0;
        });

    return (
        <>
            <TableSimple
                rowIdKey="ESHOP_ORDER_ID"
                rows={rows}
                columsLabelPrefix="ordersImport.table"
                allColumns={allColumns}
                initSort={{ column: 'ESHOP_ORDER_ID', direction: 'DESC' }}
                customRender={customRender}
            />
            <Typography gutterBottom variant="h3" sx={{ marginBottom: '20px' }}>
                {translate('ordersImport.totalCountToImport', totalCount)}
            </Typography>
            {showDetailData && <OrderDetail order={showDetailData} onClose={() => setShowDetailData(undefined)} />}
        </>
    );
};

export default ValidationTable;
